import { PublicClientApplication } from '@azure/msal-browser';

class AuthService {
    constructor(appSettings) {
        if (!appSettings) {
            throw new Error('the app settings service was not provided');
        }

        this.appSettings = appSettings;

        let msalConfig = this.GetMsalClientConfiguration();
        this.msalApplication = new PublicClientApplication(msalConfig);
    }

    // msal application object
    msalApplication;

    // settings service
    appSettings;

    // cached account info
    account;

    HandlePageLoadEvent() {
        // let exceptions bubble up to the caller to handle
        return this.msalApplication
            .handleRedirectPromise()
            .then((authResult) => {
                this.HandleRedirectResponse(authResult);
            });
    }

    HandleRedirectResponse(authResult) {
        // if this page load is redirect from the Microsoft Identity platform then the
        // authResult will be populated. Otherwise null on other page loads.

        if (authResult !== null) {
            // save the fresh account info from the result.
            this.account = authResult.account;
        } else {
            // see if we have cached accounts.
            const currentAccounts = this.msalApplication.getAllAccounts();

            if (currentAccounts === null) {
                // no cached accounts.
                // user will need to click the sign-in button and redirect to login.
                return;
            } else if (currentAccounts.length > 1) {
                // there are some situations where the user may have multiple (different) cached logins.
                // this code sample does not cover that scenario but just logs a warning here.
                // this conditional block would need to be updated to support multiple accounts.
                // otherwise it will just grab the first one below.
                console.warn(
                    'Multiple accounts detected in MSAL account cache.'
                );
                this.account = currentAccounts[0];
            } else if (currentAccounts.length === 1) {
                // we have exactly 1 cached account.
                // set the account info. user may not need to sign in.
                this.account = currentAccounts[0];
            }
        }
    }

    GetAccount(account) {
        if (account) {
            // save the fresh account info from the result.
            this.account = account;
        } else if (!this.account) {
            // see if we have cached accounts.
            const currentAccounts = this.msalApplication.getAllAccounts();

            if (currentAccounts === null) {
                // no cached accounts.
                // user will need to click the sign-in button and redirect to login.
                return;
            } else if (currentAccounts.length > 1) {
                // there are some situations where the user may have multiple (different) cached logins.
                // this code sample does not cover that scenario but just logs a warning here.
                // this conditional block would need to be updated to support multiple accounts.
                // otherwise it will just grab the first one below.
                console.warn(
                    'Multiple accounts detected in MSAL account cache.'
                );
                this.account = currentAccounts[0];
            } else if (currentAccounts.length === 1) {
                // we have exactly 1 cached account.
                // set the account info. user may not need to sign in.
                this.account = currentAccounts[0];
            }
        }
        return this.account;
    }

    GetMsalClientConfiguration() {
        return {
            auth: {
                clientId: this.appSettings.GetMsalClientId(),
                authority: this.appSettings.GetMsalTenantAuthorityUri(),
                redirectUri: this.appSettings.GetLoginRedirectUri(),
                knownAuthorities: this.appSettings.GetKnownAuthorities(),
                validateAuthority: false,
                postLogoutRedirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: this.appSettings.GetMsalCacheLocation(),
                storeAuthStateInCookie:
                    this.appSettings.GetMsalStoreAuthInCookie(),
            },
            loginRequest: {
                scopes: [this.appSettings.GetMsalClientScope()],
                prompt: 'select_account',
                //prompt: "login"
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level, message, containsPii) => {
                        if (containsPii) {
                            return;
                        }
                        //switch (level) {
                        //    case LogLevel.Error:
                        //        console.error(message);
                        //        return;
                        //    case LogLevel.Info:
                        //        console.info(message);
                        //        return;
                        //    case LogLevel.Verbose:
                        //        console.debug(message);
                        //        return;
                        //    case LogLevel.Warning:
                        //    default:
                        //        console.warn(message);
                        //        return;
                        //}
                    },
                    piiLoggingEnabled: false,
                },
                windowHashTimeout: 60000,
                iframeHashTimeout: 6000,
                loadFrameTimeout: 0,
            },
        };
    }

    async GetTokenSilent() {
        const account = this.GetAccount(null);

        let tokenRequest = {
            account: account,
            //scopes: this.appSettings.GetMsalScopes()
            scopes: [this.appSettings.GetMsalClientScope()],
        };
        try {
            const token = await this.msalApplication.acquireTokenSilent(
                tokenRequest
            );
            // msal will return the cached token if present, or call to get a new one
            // if it is expired or near expiring.
            return token;
        } catch (err) {
            //error occurred, redirect to get token
            console.error(err);
            this.GetTokenRedirect();
        }
    }

    GetTokenPopup() {
        const account = this.GetAccount(null);

        let tokenRequest = {
            account: account,
            //scopes: this.appSettings.GetMsalScopes()
            scopes: [this.appSettings.GetMsalClientScope()],
        };

        // msal will return the cached token if present, or call to get a new one
        // if it is expired or near expiring.
        return this.msalApplication.acquireTokenPopup(tokenRequest);
    }

    GetTokenRedirect() {
        const account = this.GetAccount(null);

        let tokenRequest = {
            account: account,
            //scopes: this.appSettings.GetMsalScopes()
            scopes: [this.appSettings.GetMsalClientScope()],
        };

        // msal will return the cached token if present, or call to get a new one
        // if it is expired or near expiring.
        return this.msalApplication.acquireTokenRedirect(tokenRequest);
    }

    SignIn() {
        let loginRedirectRequestPayload = {
            scopes: [this.appSettings.GetMsalClientScope()],
            prompt: 'select_account',
            //prompt: "login"
        };

        // this will redirect the web application to the Microsoft Identity platform sign in pages.
        // no code will execute after this point.
        this.msalApplication.loginRedirect(loginRedirectRequestPayload);
    }

    SignOut() {
        if (!this.GetAccount(null)) {
            // no cached login to signout
            return;
        }
        let accountInfo = this.msalApplication.getAccountByHomeId(
            this.account?.homeAccountId
        );
        //let accountInfo = this.msalApplication.getAccountByUsername(this.account?.username || this.account?.idTokenClaims?.email);

        if (accountInfo !== null) {
            this.msalApplication.logoutRedirect().catch(e => {
                console.error(e);
            });
        }
    }
}

export default AuthService;
